import React, {
  FC,
  HTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import Mask from "public/shared/masks/hexagon.png";
import {
  Button,
  CircularProgress,
  Drawer,
  Modal,
  Tooltip,
} from "@dustlabs/web/core";
import {
  CloseIcon,
  DownloadIcon,
  GalleryAddIcon,
  GalleryIcon,
  HexagonIcon,
  InfoIcon,
  SquareIcon,
  Y00tsLogo,
} from "@dustlabs/web/icons";
import { NftName, OptionToggle } from "@deid/components";
import { AttributeList, ImageShimmer } from "@shared/components";
import { useWindowSize } from "@dustlabs/web/hooks";
import { useAppConfig, useBreakpoint } from "@shared/hooks";
import { useApplyMetadata } from "@deid/helpers";
import { METADATA_URL } from "@deid/constants";
import { NftMetadata, Preview } from "@deid/types";
import { GLOBAL_CONFIG } from "@shared/constants";
import { ProjectConfig } from "@shared/types";

interface Props extends HTMLAttributes<HTMLDivElement> {
  nftMetadata: NftMetadata;
  callback?: () => Promise<void>;
  isPolygon?: boolean;
}

const NftDetailModal: FC<Props> = ({
  nftMetadata,
  className,
  callback,
  isPolygon,
  ...componentProps
}: Props) => {
  const { STATIC_URL } = GLOBAL_CONFIG as ProjectConfig;
  const { path } = useAppConfig("bridge");
  const isWeb = useBreakpoint("2xl");
  const [_, winHeight] = useWindowSize();
  const showModal = useMemo(() => winHeight > 982 || isWeb, [winHeight]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  const handleOpen = () => {
    setDrawerOpen(true);
  };

  const handleClose = () => {
    setDrawerOpen(false);
  };

  const [version, setVersion] = useState<number>(0);
  const versionOptions = useMemo(
    () => [
      <Y00tsLogo
        variant="full"
        height={20}
        width={40}
        className={`${
          version === 0 ? "fill-darkFontPrimary" : "fill-lightFontPrimary"
        }`}
      />,
      <svg
        width="51"
        height="14"
        viewBox="0 0 51 14"
        className={`w-[40px] h-[20px] ${
          version === 1 ? "fill-darkFontPrimary" : "fill-lightFontPrimary"
        }`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0.294459 1.00982C0.147536 0.504258 0.527093 0 1.05357 0H3.29806C3.66891 0 3.98935 0.258322 4.07096 0.620074C4.51215 2.57563 5.01139 4.49502 5.60679 6.41561C6.00174 4.50524 6.37148 2.60701 6.65716 0.681338C6.71504 0.291244 7.04835 0 7.44272 0H9.3326C9.80319 0 10.1697 0.4085 10.1223 0.876701C9.73664 4.68865 9.38255 9.62681 6.75447 12.3353C5.1255 14.0142 2.82376 14.0232 0.788854 14.0025C0.353079 13.998 0 13.6432 0 13.2074C0 12.7154 0 12.1893 0 11.7443C0 11.3085 0.353186 10.964 0.788944 10.9582C1.84591 10.944 3.01556 10.7816 2.69703 9.55768C2.07443 7.1653 0.854865 2.93817 0.294459 1.00982Z" />
        <path d="M15.04 6.63C15.544 6.21 15.91 5.868 16.138 5.604C16.378 5.34 16.522 5.136 16.57 4.992C16.63 4.848 16.66 4.692 16.66 4.524C16.66 4.512 16.66 4.5 16.66 4.488C16.66 4.476 16.66 4.464 16.66 4.452C16.66 3.924 16.564 3.528 16.372 3.264C16.18 2.988 15.832 2.826 15.328 2.778C15.076 2.754 14.872 2.784 14.716 2.868C14.56 2.952 14.422 3.132 14.302 3.408C14.2714 3.48267 14.2423 3.56886 14.2147 3.66658C14.066 4.19167 13.5972 4.58269 13.0549 4.5212L11.5628 4.35199C11.0028 4.28848 10.6069 3.76997 10.8196 3.248C10.8702 3.12381 10.927 2.99715 10.99 2.868C11.23 2.364 11.422 2.004 11.566 1.788C11.89 1.284 12.508 0.882 13.42 0.582C14.332 0.27 15.412 0.113999 16.66 0.113999C16.96 0.113999 17.35 0.251999 17.83 0.527999C18.322 0.792 18.76 1.29 19.144 2.022C19.528 2.742 19.69 3.738 19.63 5.01C19.618 5.214 19.558 5.448 19.45 5.712C19.342 5.976 19.078 6.342 18.658 6.81C18.25 7.278 17.638 7.824 16.822 8.448C16.3243 8.82405 16.6075 9.61731 17.2309 9.59306L18.7332 9.5346C19.321 9.51173 19.8014 9.9987 19.7707 10.5862L19.6813 12.2931C19.6528 12.8369 19.1945 13.2579 18.6503 13.2403L11.8298 13.0194C11.243 13.0004 10.7988 12.4824 10.8694 11.8996L10.954 11.202C11.098 10.386 11.344 9.714 11.692 9.186C12.052 8.658 12.424 8.25 12.808 7.962C13.204 7.674 13.678 7.386 14.23 7.098C14.398 7.026 14.548 6.948 14.68 6.864C14.824 6.78 14.944 6.702 15.04 6.63Z" />
        <path d="M20.5921 6.576C20.5921 5.82 20.6521 5.244 20.7721 4.848C20.9041 4.452 21.1501 3.966 21.5101 3.39C21.7501 3.018 22.1041 2.592 22.5721 2.112C23.0521 1.62 23.5381 1.2 24.0301 0.852C24.5221 0.492 24.9061 0.3 25.1821 0.275999C26.0461 0.204 26.7421 0.24 27.2701 0.384C27.8101 0.516 28.2061 0.702 28.4581 0.941999C28.7221 1.17 28.9741 1.458 29.2141 1.806C29.238 1.85369 29.2734 1.89476 29.2995 1.94127C29.3011 1.94418 29.3027 1.94709 29.3041 1.95C29.3641 2.046 29.5021 2.376 29.7181 2.94C29.9341 3.504 30.1261 4.134 30.2941 4.83C30.4741 5.526 30.5641 6.15 30.5641 6.702C30.5641 7.494 30.4381 8.334 30.1861 9.222C29.9461 10.098 29.6521 10.842 29.3041 11.454C29.2201 11.598 28.9681 11.832 28.5481 12.156C28.1401 12.468 27.6661 12.75 27.1261 13.002C26.5861 13.242 26.0701 13.362 25.5781 13.362C25.0981 13.362 24.5881 13.254 24.0481 13.038C23.5201 12.822 23.0521 12.564 22.6441 12.264C22.2361 11.964 21.9661 11.694 21.8341 11.454C21.4741 10.818 21.1741 10.062 20.9341 9.186C20.7061 8.298 20.5921 7.428 20.5921 6.576ZM25.5781 8.916C25.8541 8.916 26.1241 8.856 26.3881 8.736C26.6641 8.604 26.8441 8.472 26.9281 8.34C27.1441 8.028 27.3241 7.728 27.4681 7.44C27.6241 7.14 27.7021 6.858 27.7021 6.594C27.7021 6.294 27.6241 5.934 27.4681 5.514C27.3241 5.094 27.1981 4.794 27.0901 4.614C27.0061 4.47 26.7901 4.302 26.4421 4.11C26.1061 3.918 25.8121 3.822 25.5601 3.822C25.2961 3.822 25.0501 3.942 24.8221 4.182C24.6061 4.41 24.4201 4.698 24.2641 5.046C24.1561 5.25 24.0721 5.502 24.0121 5.802C23.9641 6.102 23.9401 6.408 23.9401 6.72C23.9401 7.428 24.0661 7.986 24.3181 8.394C24.3661 8.478 24.5221 8.586 24.7861 8.718C25.0621 8.85 25.3261 8.916 25.5781 8.916Z" />
        <path d="M31.2587 9.366C31.2587 8.766 31.3787 8.22 31.6187 7.728C31.6919 7.57792 31.7746 7.43398 31.8668 7.29619C32.2053 6.79018 32.2317 6.07872 31.9305 5.54964C31.9037 5.50248 31.8777 5.4546 31.8527 5.406C31.6607 5.01 31.5647 4.686 31.5647 4.434C31.5647 3.21 31.6067 2.538 31.6907 2.418C32.0507 1.878 32.5607 1.362 33.2207 0.87C33.8807 0.366 34.4507 0.0899997 34.9307 0.0420003C35.7107 -0.0420001 36.4787 -2.38419e-07 37.2347 0.168C37.9907 0.336 38.5487 0.6 38.9087 0.96C38.9687 1.02 39.0827 1.224 39.2507 1.572C39.4307 1.908 39.5867 2.292 39.7187 2.724C39.8627 3.144 39.9347 3.534 39.9347 3.894C39.9347 4.374 39.8327 4.842 39.6287 5.298C39.5934 5.37951 39.5556 5.459 39.515 5.53647C39.2329 6.07613 39.2584 6.79147 39.5894 7.30264C39.6768 7.43765 39.7559 7.57944 39.8267 7.728C40.0667 8.22 40.1867 8.76 40.1867 9.348C40.1867 9.984 40.0607 10.65 39.8087 11.346C39.5567 12.03 39.2267 12.558 38.8187 12.93C38.7467 13.002 38.5367 13.098 38.1887 13.218C37.8407 13.35 37.4447 13.464 37.0007 13.56C36.5567 13.656 36.1427 13.704 35.7587 13.704C35.3867 13.704 34.9127 13.596 34.3367 13.38C33.7607 13.164 33.2267 12.9 32.7347 12.588C32.2427 12.276 31.9187 11.988 31.7627 11.724C31.6187 11.484 31.4987 11.178 31.4027 10.806C31.3067 10.422 31.2587 9.942 31.2587 9.366ZM35.6327 5.478C35.8607 5.478 36.2507 5.37 36.8027 5.154C36.9587 5.07 37.0667 4.914 37.1267 4.686C37.1987 4.446 37.2347 4.206 37.2347 3.966C37.2347 3.738 37.1507 3.51 36.9827 3.282C36.8147 3.042 36.6047 2.85 36.3527 2.706C36.1007 2.562 35.8607 2.496 35.6327 2.508C35.3207 2.544 35.0627 2.676 34.8587 2.904C34.6547 3.12 34.5107 3.366 34.4267 3.642C34.3547 3.918 34.3547 4.152 34.4267 4.344C34.5467 4.704 34.6967 4.986 34.8767 5.19C35.0687 5.382 35.3207 5.478 35.6327 5.478ZM34.3007 8.592C34.0727 9.192 34.0727 9.678 34.3007 10.05C34.3727 10.206 34.5947 10.386 34.9667 10.59C35.3387 10.794 35.6507 10.896 35.9027 10.896C36.1787 10.896 36.4607 10.848 36.7487 10.752C37.0487 10.656 37.2647 10.524 37.3967 10.356C37.4447 10.296 37.4927 10.146 37.5407 9.906C37.5887 9.654 37.6127 9.438 37.6127 9.258C37.6127 8.994 37.5647 8.736 37.4687 8.484C37.3847 8.232 37.2347 8.058 37.0187 7.962C36.8867 7.89 36.7127 7.818 36.4967 7.746C36.2807 7.674 36.0827 7.638 35.9027 7.638C35.4587 7.638 35.0987 7.728 34.8227 7.908C34.5587 8.076 34.3847 8.304 34.3007 8.592Z" />
        <path d="M41.9124 10.464C41.3602 10.464 40.9124 10.0163 40.9124 9.464V7.37354C40.9124 7.15951 40.9811 6.95112 41.1084 6.77902L45.6166 0.681489C45.8052 0.426459 46.1036 0.275999 46.4207 0.275999H48.3364C48.8887 0.275999 49.3364 0.723715 49.3364 1.276V6.67605C49.3364 7.09797 49.6785 7.44 50.1004 7.44C50.5015 7.44 50.8343 7.75018 50.8625 8.15029L50.9315 9.13098C50.9659 9.6196 50.6194 10.0528 50.1352 10.1265C49.6758 10.1964 49.3364 10.5913 49.3364 11.0559V12.1988C49.3364 12.7152 48.9433 13.1466 48.4292 13.1945L47.4532 13.2855C46.8959 13.3374 46.4045 12.9218 46.3632 12.3636L46.291 11.3901C46.2524 10.8679 45.8174 10.464 45.2938 10.464H41.9124ZM46.7099 6.82337C46.68 6.19561 45.8728 5.96086 45.5109 6.47471C45.211 6.9006 45.4994 7.48982 46.0198 7.51414C46.4085 7.5323 46.7284 7.21204 46.7099 6.82337Z" />
      </svg>,
    ],
    [version]
  );

  const [preview, setPreview] = useState<Preview>(Preview.DEFAULT);
  const previewOptions = useMemo(
    () => [
      <SquareIcon
        width={32}
        height={32}
        rectAttributes={{
          className: `${
            preview === Preview.DEFAULT
              ? "stroke-darkFontPrimary"
              : "stroke-lightFontPrimary"
          }`,
        }}
      />,
      <HexagonIcon
        width={32}
        height={32}
        pathAttributes={{
          className: `${
            preview === Preview.HEXAGON
              ? "stroke-darkFontPrimary"
              : "stroke-lightFontPrimary"
          }`,
        }}
      />,
    ],
    [preview]
  );

  useEffect(() => {
    if (isPolygon) {
      setVersion(0);
    } else {
      if (nftMetadata?.image.includes("y2084")) {
        setVersion(1);
      } else {
        setVersion(0);
      }
    }

    setPreview(Preview.DEFAULT);
  }, [nftMetadata, drawerOpen]);

  const BASE_IMAGE = useMemo(() => `${METADATA_URL}${nftMetadata.id}.png`, []);

  const nftImage = useMemo(() => {
    switch (version) {
      case 1:
        return `${STATIC_URL}/y2084/${nftMetadata.id}.png`;
      default:
      case 0:
        return BASE_IMAGE;
    }
  }, [version, nftMetadata]);

  const [hexagonCrop, setHexagonCrop] = useState<string>("");
  const [cropLoading, setCropLoading] = useState<boolean>(false);

  const crop = useCallback(() => {
    setCropLoading(true);
    const offScreenCanvas = document.createElement("canvas");
    offScreenCanvas.width = Mask.width;
    offScreenCanvas.height = Mask.height;
    var ctx = offScreenCanvas.getContext("2d");
    if (ctx) {
      var imageObj1 = new window.Image();
      imageObj1.src = Mask.src;
      imageObj1.onload = () => {
        const selectedImage = new window.Image();
        selectedImage.src = nftImage;
        selectedImage.width = Mask.width;
        selectedImage.height = Mask.height;
        selectedImage.crossOrigin = "anonymous";

        selectedImage.onload = () => {
          if (ctx) {
            ctx.drawImage(imageObj1, 0, 0);
            ctx.globalCompositeOperation = "source-in";
            ctx.drawImage(selectedImage, 0, 0, Mask.width, Mask.height);
            setHexagonCrop(offScreenCanvas.toDataURL("image/png", 1));
            setCropLoading(false);
          }
        };
      };
    }
  }, [nftImage]);

  useEffect(() => {
    if (preview === Preview.HEXAGON && nftImage !== "") {
      crop();
    }
  }, [preview, crop, nftImage]);

  const { mutateAsync: applyMetadata, isLoading: isLoadingApplyMetadata } =
    useApplyMetadata(callback);

  const handleApplyMetadata = async () => {
    await applyMetadata({
      tokenId: nftMetadata.id,
      perspective: 0, // disable perspective for now
    });
  };

  const renderContent = () => (
    <div className="w-full flex flex-col lg:flex-row lg:justify-center gap-5 heightSmall:gap-5 md:gap-10 py-8 heightSmall:pb-5 heightSmall:pt-5 md:py-12 px-3 lg:px-16 overflow-auto rounded-t-2xl lg:rounded-none scrollbar-none">
      <NftName nftMetadata={nftMetadata} className="flex lg:hidden" />
      <div className="flex flex-col gap-3 heightSmall:gap-3 md:gap-3">
        <div className="w-full aspect-square relative md:min-w-[401px]">
          {preview === Preview.HEXAGON && hexagonCrop ? (
            <ImageShimmer
              src={cropLoading ? "" : hexagonCrop}
              fill
              alt={`${nftMetadata.id}`}
            />
          ) : (
            <ImageShimmer
              src={nftImage}
              fallbackImage={BASE_IMAGE}
              fallbackOverlay="/y00ts/y2084/fallback-overlay.png"
              fill
              alt={`${nftMetadata.id}`}
            />
          )}
        </div>
        <div className="flex flex-col heightXs:flex-col md:flex-row gap-3">
          {/* <div className="flex gap-3 flex-1"> */}
          {/* <div className="flex-1">
              <div className="relative hidden md:flex justify-center items-center gap-1 text-center text-fontSecondary text-xs mb-1">
                <span>Version</span>
                <Tooltip
                  content={[
                    "These version options do not affect the image displayed on the NFT.",
                  ]}
                  className="!bg-lightBackground border border-lightBorder top-7 md:top-0 
              -left-[250px] lg:-left-[160px] min-h-[70px] w-[320px] p-2 rounded-lg text-xs"
                >
                  <InfoIcon
                    width={12}
                    height={12}
                    className="fill-transparent [&>path]:stroke-fontSecondary"
                  />
                </Tooltip>
              </div>
              <OptionToggle
                value={version}
                handleChange={(newValue) => {
                  setVersion(newValue as number);
                  if (preview === Preview.HEXAGON && newValue === 1) {
                    setPreview(Preview.DEFAULT);
                  }
                }}
                options={versionOptions}
                highlight={undefined}
                disabled={isPolygon}
              />
            </div> */}

          {/* <div className="flex-1">
              <div className="relative hidden md:flex justify-center items-center gap-1 text-center text-fontSecondary text-xs mb-1">
                <span>Preview</span>
                <Tooltip
                  content={[
                    "These preview options do not affect the image displayed on the NFT, instead are meant to show you what it will look like in the NFT vs Twitter Hexagon.",
                  ]}
                  className="!bg-lightBackground border border-lightBorder top-7 md:top-0 
              -left-[250px] lg:-left-[160px] min-h-[70px] w-[320px] p-2 rounded-lg text-xs"
                >
                  <InfoIcon
                    width={12}
                    height={12}
                    className="fill-transparent [&>path]:stroke-fontSecondary"
                  />
                </Tooltip>
              </div>
              <OptionToggle
                value={preview === Preview.HEXAGON ? 1 : 0}
                handleChange={(newValue) =>
                  setPreview(newValue === 1 ? Preview.HEXAGON : Preview.DEFAULT)
                }
                options={previewOptions}
                disabled={isPolygon || version === 1}
              />
            </div> */}
          {/* </div> */}
          {/* <div className="flex-1 flex items-end"> */}
          <Link
            className="w-full heightXs:w-full lg:w-full h-[42px] flex flex-row justify-center items-center bg-lightSurface border border-lightFontPrimary rounded-lg"
            href={
              isPolygon && preview === Preview.HEXAGON ? hexagonCrop : nftImage
            }
            rel="noreferrer"
            target="_blank"
          >
            <div className="flex flex-row text-center justify-center items-center gap-1">
              <DownloadIcon className="w-4 h-4 fill-lightFontPrimary mb-[2px]" />

              <h3 className="text-sm font-bold uppercase">Download</h3>
            </div>
          </Link>
          {/* </div> */}
        </div>
        <div className="flex flex-col heightSmall:flex-row gap-3">
          {isPolygon && (
            <Button
              full
              onClick={() => window.open(path, "_blank", "noopener noreferrer")}
              className="h-[42px] bg-primary flex justify-center items-center gap-2 font-bold text-darkFontPrimary uppercase rounded-lg"
            >
              Bridge to Ethereum
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 lg:max-w-[560px] flex flex-col gap-3">
        <NftName nftMetadata={nftMetadata} className="hidden lg:flex" />
        <AttributeList item={nftMetadata} overview />
        {!isPolygon && (
          <div className="flex flex-col gap-1 pt-3 heightSmall:pt-0 heightSmall:pb-3 lg:pt-6 pl-1">
            <div className="text-sm font-bold">How it works:</div>
            <div className="text-sm text-fontSecondary">
              Toggle your {GLOBAL_CONFIG.NFT_NAME} between different versions
              and download the file on your computer. This will not change the
              NFT image on-chain.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div
        className={`absolute w-full h-[276px] z-10 cursor-pointer ${className}`}
        onClick={handleOpen}
        {...componentProps}
      />

      {showModal ? (
        <Modal
          initial={{ opacity: 0, y: "50vh" }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: "50vh" }}
          transition={{ duration: 0.35 }}
          isOpen={drawerOpen}
          handleClose={handleClose}
          className="max-w-[1283px] !w-[95vw] max-h-[85vh] !h-fit !rounded-xl !bg-lightBackground !p-0 flex"
          closeIconProps={{ className: "hidden" }}
          backdropProps={{
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            exit: { opacity: 0 },
            transition: { duration: 0.35 },
            className: "bg-opacity-50 bg-[#000]",
          }}
        >
          <motion.div
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="absolute -top-3 -right-3 cursor-pointer z-30 w-8 h-8 !rounded-full bg-[#19191C] dark:border dark:border-darkBorder flex justify-center items-center"
            onClick={handleClose}
          >
            <CloseIcon width={10} height={10} className="fill-[#D2D2D2]" />
          </motion.div>

          {renderContent()}
        </Modal>
      ) : (
        <Drawer
          anchor="bottom"
          isOpen={drawerOpen}
          handleClose={handleClose}
          className="relative max-h-[85vh] heightSmall:max-h-[90vh] max-w-[1283px] md:!w-[95vw] !h-fit !bg-lightBackground 
          flex justify-center justify-self-center rounded-t-2xl !pt-0 !lg:pt-3 pb-0 lg:pb-5"
          closeIconProps={{
            className: "hidden",
          }}
          backdropProps={{
            className: "bg-opacity-50 bg-[#000]",
          }}
        >
          <div className="absolute w-full -top-4 md:-top-3 flex justify-center md:justify-end items-center z-20">
            <Button
              text
              onClick={handleClose}
              className="relative md:absolute w-8 h-8 right-auto md:-right-3 !rounded-full bg-[#19191C] dark:border dark:border-darkBorder flex justify-center items-center"
            >
              <CloseIcon width={10} height={10} className="fill-[#D2D2D2]" />
            </Button>
          </div>
          {renderContent()}
        </Drawer>
      )}
    </>
  );
};

export default NftDetailModal;
